import { Affaire } from '../types/search.types'
import { ExpertiseCardProps, ExpertiseStatus } from '@/ui/components/molecules/ExpertiseCard/ExpertiseCard.types'

/**
 * Maps the Affaire status to ExpertiseStatus
 */
const mapAffaireStatusToExpertiseStatus = (status: string): ExpertiseStatus => {
  const statusLower = status.toLowerCase().trim()

  switch (statusLower) {
    case 'en cours':
      return 'en_cours'
    case 'terminé':
    case 'termine':
    case 'terminée':
      return 'termine'
    case 'nouveau':
      return 'nouveau'
    case 'archive':
    case 'archivé':
      return 'archive'
    case 'urgent':
      return 'urgent'
    case 'attente info':
    case 'attente d\'info':
      return 'attente_info'
    case 'validation':
      return 'validation'
    default:
      return 'en_cours' // Default fallback
  }
}

/**
 * Maps an Affaire object to ExpertiseCardProps for use in ExpertiseList/ExpertiseCard
 */
export const mapAffaireToExpertiseCard = (affaire: Affaire): ExpertiseCardProps => {
  // Extract billing information from facturation object if available
  const facturation = affaire.facturation || {}

  return {
    // Use id_unique for the id field - this is what FacadeImage and API endpoints expect
    id: affaire.id_unique,
    reference: affaire.id_unique,

    // Use type_mission as the title (e.g., "Expertise Habitation", "Expertise Dégâts des eaux")
    title: affaire.type_mission || 'Expertise Immobilière',

    status: mapAffaireStatusToExpertiseStatus(affaire.statut_affaire),
    statusDisplay: affaire.statut_affaire || 'status not found', // Pass raw backend status

    // Client information
    clientName: affaire.client
      ? `${affaire.client.firstName} ${affaire.client.lastName}`
      : undefined,

    // Expert information
    expertName: affaire.expert || 'Non assigné',

    // Responsable information
    responsableName: affaire.responsable_name,

    // Type information
    type: affaire.type_mission,

    // Address information
    address: affaire.address ? {
      street: affaire.address.number && affaire.address.street
        ? `${affaire.address.number} ${affaire.address.street}`
        : affaire.address.street,
      city: affaire.address.locality,
      postalCode: affaire.address.postcode,
    } : undefined,

    // Financial information
    amount: affaire.montant_tvac_affaire,
    currency: 'EUR',

    // Billing information from facturation object
    paid: facturation.affaire_payee === 'oui' || facturation.affaire_payee === true,
    perception: facturation.perception,
    invoiceStatus: facturation.facture_emise === 'oui' || facturation.facture_emise === true ? 'emise' : undefined,
    invoiceIssued: facturation.facture_emise === 'oui' || facturation.facture_emise === true,

    // Date information
    updatedAt: affaire.date_sortie,
    createdAt: affaire.date_sortie,

    // Display variant
    variant: 'standard',
  }
}

/**
 * Maps an array of Affaires to ExpertiseCardProps array
 */
export const mapAffairesToExpertiseCards = (affaires: Affaire[]): ExpertiseCardProps[] => {
  return affaires.map(mapAffaireToExpertiseCard)
}
